/**
 * Capacitor Background Sync Service
 * 
 * Handles background synchronization using Capacitor plugins for native mobile apps.
 * Integrates with the existing SyncService to provide seamless background sync
 * capabilities when the app is not in the foreground.
 */

import { App, AppState } from '@capacitor/app';
import { Network, ConnectionStatus } from '@capacitor/network';
import { BackgroundTask } from '@capawesome/capacitor-background-task';
import { isNativeApp } from '@/utils/platformUtils';

/**
 * Background sync configuration
 */
export interface CapacitorSyncConfig {
  // Background sync settings
  enableBackgroundSync: boolean;
  backgroundSyncInterval: number; // milliseconds
  maxBackgroundDuration: number; // milliseconds
  
  // Network settings
  enableNetworkDetection: boolean;
  syncOnNetworkRestore: boolean;
  
  // App state settings
  syncOnAppResume: boolean;
  syncOnAppBackground: boolean;
  backgroundSyncDelay: number; // delay before starting background sync
  
  // Battery optimization
  respectBatteryOptimization: boolean;
  lowBatteryThreshold: number; // percentage
}

/**
 * Background sync events
 */
export type CapacitorSyncEvent = 
  | { type: 'background_sync_started'; data: { trigger: string } }
  | { type: 'background_sync_completed'; data: { duration: number; operations: number } }
  | { type: 'background_sync_failed'; data: { error: string; trigger: string } }
  | { type: 'app_state_changed'; data: { state: string; isActive: boolean } }
  | { type: 'network_status_changed'; data: { connected: boolean; connectionType: string } }
  | { type: 'background_task_started'; data: { taskId: string } }
  | { type: 'background_task_finished'; data: { taskId: string; success: boolean } };

export type CapacitorSyncEventListener = (event: CapacitorSyncEvent) => void;

/**
 * Capacitor Background Sync Service
 */
export class CapacitorBackgroundSyncService {
  private config: CapacitorSyncConfig;
  private eventListeners: Set<CapacitorSyncEventListener> = new Set();
  private isInitialized = false;
  private currentBackgroundTask: string | null = null;
  private networkStatus: ConnectionStatus | null = null;
  private appState: AppState | null = null;
  private backgroundSyncTimeout: NodeJS.Timeout | null = null;

  constructor(config: Partial<CapacitorSyncConfig> = {}) {
    this.config = {
      enableBackgroundSync: true,
      backgroundSyncInterval: 300000, // 5 minutes
      maxBackgroundDuration: 30000, // 30 seconds
      enableNetworkDetection: true,
      syncOnNetworkRestore: true,
      syncOnAppResume: true,
      syncOnAppBackground: true,
      backgroundSyncDelay: 2000, // 2 seconds
      respectBatteryOptimization: true,
      lowBatteryThreshold: 20, // 20%
      ...config
    };
  }

  /**
   * Initialize the Capacitor background sync service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    // Only initialize on native platforms
    if (!isNativeApp()) {

      return;
    }

    try {


      // Initialize network monitoring
      if (this.config.enableNetworkDetection) {
        await this.initializeNetworkMonitoring();
      }

      // Initialize app state monitoring
      await this.initializeAppStateMonitoring();

      // Initialize background runner
      if (this.config.enableBackgroundSync) {
        await this.initializeBackgroundRunner();
      }

      this.isInitialized = true;

    } catch (error) {
      console.error('❌ Failed to initialize Capacitor Background Sync Service:', error);
      throw error;
    }
  }

  /**
   * Add event listener
   */
  addEventListener(listener: CapacitorSyncEventListener): void {
    this.eventListeners.add(listener);
  }

  /**
   * Remove event listener
   */
  removeEventListener(listener: CapacitorSyncEventListener): void {
    this.eventListeners.delete(listener);
  }

  /**
   * Emit event to all listeners
   */
  private emitEvent(event: CapacitorSyncEvent): void {
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in Capacitor sync event listener:', error);
      }
    });
  }

  /**
   * Initialize network monitoring
   */
  private async initializeNetworkMonitoring(): Promise<void> {
    try {
      // Get initial network status
      this.networkStatus = await Network.getStatus();
      


      // Listen for network changes
      Network.addListener('networkStatusChange', (status) => {
        const wasConnected = this.networkStatus?.connected || false;
        const isConnected = status.connected;

        this.networkStatus = status;



        this.emitEvent({
          type: 'network_status_changed',
          data: {
            connected: isConnected,
            connectionType: status.connectionType
          }
        });

        // Trigger sync when network is restored
        if (!wasConnected && isConnected && this.config.syncOnNetworkRestore) {
          this.scheduleBackgroundSync('network_restored');
        }
      });
    } catch (error) {
      console.error('Failed to initialize network monitoring:', error);
    }
  }

  /**
   * Initialize app state monitoring
   */
  private async initializeAppStateMonitoring(): Promise<void> {
    try {
      // Get initial app state
      this.appState = await App.getState();
      


      // Listen for app state changes
      App.addListener('appStateChange', (state) => {
        const wasActive = this.appState?.isActive || false;
        const isActive = state.isActive;

        this.appState = state;



        this.emitEvent({
          type: 'app_state_changed',
          data: {
            state: isActive ? 'active' : 'background',
            isActive
          }
        });

        // Handle app state transitions
        if (!wasActive && isActive && this.config.syncOnAppResume) {
          // App resumed, trigger sync
          this.scheduleBackgroundSync('app_resumed');
        } else if (wasActive && !isActive && this.config.syncOnAppBackground) {
          // App went to background, schedule background sync
          this.scheduleBackgroundSync('app_backgrounded', this.config.backgroundSyncDelay);
        }
      });
    } catch (error) {
      console.error('Failed to initialize app state monitoring:', error);
    }
  }

  /**
   * Initialize background runner
   */
  private async initializeBackgroundRunner(): Promise<void> {
    try {

      // Note: Background runner implementation will be completed when the API is stable
    } catch (error) {
      console.error('Failed to initialize background runner:', error);
    }
  }

  /**
   * Schedule background sync
   */
  private scheduleBackgroundSync(trigger: string, delay: number = 0): void {
    if (!this.config.enableBackgroundSync) return;

    // Clear existing timeout
    if (this.backgroundSyncTimeout) {
      clearTimeout(this.backgroundSyncTimeout);
    }

    this.backgroundSyncTimeout = setTimeout(async () => {
      await this.performBackgroundSync(trigger);
    }, delay);
  }

  /**
   * Perform background sync
   */
  private async performBackgroundSync(trigger: string): Promise<void> {
    if (!this.networkStatus?.connected) {

      return;
    }

    try {


      this.emitEvent({
        type: 'background_sync_started',
        data: { trigger }
      });

      // Start background task to prevent app suspension
      const taskId = await this.startBackgroundTask();

      try {
        // Trigger the main sync service
        const { syncService } = await import('@/services/offline');
        await syncService.performSync();

        this.emitEvent({
          type: 'background_sync_completed',
          data: {
            duration: 0, // Will be calculated by sync service
            operations: 0 // Will be provided by sync service
          }
        });


      } finally {
        // Always finish the background task
        if (taskId) {
          await this.finishBackgroundTask(taskId);
        }
      }
    } catch (error) {
      console.error('❌ Background sync failed:', error);
      this.emitEvent({
        type: 'background_sync_failed',
        data: {
          error: error instanceof Error ? error.message : 'Unknown error',
          trigger
        }
      });
    }
  }

  /**
   * Start background task
   */
  private async startBackgroundTask(): Promise<string | null> {
    try {
      const taskId = await BackgroundTask.beforeExit(async () => {
        // This callback will be called when the app is about to be suspended

      });

      this.currentBackgroundTask = taskId;

      this.emitEvent({
        type: 'background_task_started',
        data: { taskId }
      });

      return taskId;
    } catch (error) {
      console.error('Failed to start background task:', error);
      return null;
    }
  }

  /**
   * Finish background task
   */
  private async finishBackgroundTask(taskId: string): Promise<void> {
    try {
      await BackgroundTask.finish({ taskId });

      this.emitEvent({
        type: 'background_task_finished',
        data: { taskId, success: true }
      });

      if (this.currentBackgroundTask === taskId) {
        this.currentBackgroundTask = null;
      }


    } catch (error) {
      console.error('Failed to finish background task:', error);
      this.emitEvent({
        type: 'background_task_finished',
        data: { taskId, success: false }
      });
    }
  }

  /**
   * Manually trigger background sync
   */
  async triggerSync(): Promise<void> {
    await this.performBackgroundSync('manual');
  }

  /**
   * Get current configuration
   */
  getConfig(): CapacitorSyncConfig {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<CapacitorSyncConfig>): void {
    this.config = { ...this.config, ...updates };
  }

  /**
   * Cleanup resources
   */
  async cleanup(): Promise<void> {
    if (this.backgroundSyncTimeout) {
      clearTimeout(this.backgroundSyncTimeout);
      this.backgroundSyncTimeout = null;
    }

    if (this.currentBackgroundTask) {
      await this.finishBackgroundTask(this.currentBackgroundTask);
    }

    // Remove all listeners
    Network.removeAllListeners();
    App.removeAllListeners();

    this.eventListeners.clear();
    this.isInitialized = false;


  }
}

// Export singleton instance
export const capacitorBackgroundSyncService = new CapacitorBackgroundSyncService();
